/* eslint-disable no-console */
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Typography, message, Icon, Button, Alert } from 'antd';
import { subscribe } from 'alfa';
import uuidv4 from 'uuid/v4';
import semaphore from 'semaphore';
import { withTranslation } from 'react-i18next';
import { Redirect } from 'react-router-dom';

import money from '../../helpers/money';
import BatchStats from './components/stats';
import CreationForm from './components/creation-form';
import BatchRepo from '../../repository/batch';
import URL from '../../helpers/url';
import { transactionStatuses, batchType, csvExample } from '../../helpers/constants';
import Config from '../../config/config';
import Security from '../../helpers/security';
import { createSocket } from '../../helpers/progressSocket';
import { validate, csvOptions } from './components/creation-helpers';
import { summaryColumns } from './components/SummaryColumns';

const maxConcurrency = 1;
const batchLimit = 1;
const { Title } = Typography;

const processedStatuses = [transactionStatuses.success, transactionStatuses.processing, transactionStatuses.failed];

class BatchNew extends Component { // eslint-disable-line fp/no-class
  constructor(props) {
    super(props);
    this.state = {
      id: uuidv4(),
      description: null,
      items: [],
      itemsMap: {},
      processing: false,
      processed: [],
      redirect: false,
      submitted: false,
    };
    this.websocket = null;
  }

  componentDidMount() {
    const { id } = this.state;
    const channel = 'batch_item';
    const subscribeData = {
      action: 'subscribe',
      channel,
      auth: Security.getToken(),
      id,
    };
    const onMessage = (socketData) => {
      const { itemsMap, submitted } = this.state;
      const { business } = this.props;
      const { data: transactionData = {} } = socketData;
      if (!submitted || (socketData.businessId !== business.id)) { return; }
      const { [transactionData.transaction_id]: item } = itemsMap;
      if (item && processedStatuses.includes(transactionData.status)) {
        const { status: itemStatus } = item;
        itemsMap[item.id] = { ...item, status: transactionData.status };
        this.setState((prevState) => ({
          itemsMap,
          processed: (itemStatus) ? prevState.processed.map(
            (processedItem) => (
              (processedItem.id === transactionData.transaction_id)
                ? { ...processedItem, ...transactionData }
                : processedItem
            ),
          ) : [...prevState.processed, { ...item, ...transactionData }],
          redirect: !itemStatus && ((prevState.processed.length + 1) === prevState.items.length),
        }));
      }
    };
    const socketArgs = {
      url: Config.socket_url,
      channel,
      subscribeData,
      onMessage,
    };
    this.websocket = createSocket(socketArgs);
  }

  componentWillUnmount() {
    if (this.websocket) { this.websocket.close(); }
  }

    uploadInBulk = async () => {
      const { id, items } = this.state;
      const { business, account, t } = this.props;
      const mapItem = ({ id: mapItemId, amount: itemAmount, currency, name: destination_name, lana_id: destination_url, destination: { gateway_uri: destination_uri }, description, signature, now: value_ts }) => ({
        id: mapItemId,
        currency,
        destination_name,
        destination_url,
        destination_uri,
        description,
        signature,
        value_ts,
        amount: money.toTransact(itemAmount, currency),
      });
      const reduceItems = (result, item) => ({
        ...result,
        [item.id]: item,
      });
      const bulkItems = items.map(mapItem);
      const itemsMap = bulkItems.reduce(reduceItems, {});
      try {
        const { data: { success, errored_items = [] } } = await BatchRepo.bulkItems({ business, account, batchID: id, items: bulkItems });
        let processed = [];
        if (!success) {
          processed = items.reduce((acc, val) => {
            if (errored_items.includes(val.id)) {
              acc.push({ ...val, status: transactionStatuses.failed });
            }
            return acc;
          }, []);
          message.error(t('error.creating_batch'));
        }
        this.setState((prevState) => ({
          itemsMap,
          submitted: true,
          processed: [...prevState.processed, ...processed],
        }));
      } catch (error) {
        console.error('ERROR: BatchNew: BatchRepo.bulkItems()', error);
      }
    };

    uploadInBatch = () => {
      const batchSemaphore = semaphore(maxConcurrency);
      const { id, items } = this.state;
      const { business, account, t } = this.props;
      items.forEach((item, key) => {
        batchSemaphore.take(async () => {
          const { id: itemId, amount: itemAmount, currency, name: destination_name, lana_id: destination_url, destination: { gateway_uri: destination_uri }, description, signature, now: valueTs } = items[key];
          let batchItem = { id: itemId, currency, destination_name, destination_url, destination_uri, description, signature, value_ts: valueTs, amount: money.toTransact(itemAmount, currency) };
          try {
            const { data } = await BatchRepo.addItem({ business, account, batchID: id, item: batchItem });
            batchItem = { ...batchItem, ...data };
          } catch (error) {
            const { response } = error;
            if (response.data) {
              batchItem = { ...batchItem, ...response.data };
            }
            console.error('ERROR: BatchRepo.addItem(): ', error);
            message.error(t('error.creating_batchItem'));
          } finally {
            this.setState((prevState) => ({
              processed: [
                ...prevState.processed,
                {
                  dest_url: batchItem.destination_url,
                  dest_name: batchItem.destination_name,
                  status: batchItem.status,
                  desc: batchItem.description,
                  amount: batchItem.amount,
                  currency: batchItem.currency,
                  transaction_id: batchItem.transaction_id,
                  response: batchItem.response,
                },
              ],
              redirect: prevState.processed.length + 1 === prevState.items.length,
            }));
            batchSemaphore.leave();
          }
        });
      });
    }

    processItems = () => {
      const { items } = this.state;
      const uploadFunction = (items.length > batchLimit) ? this.uploadInBulk : this.uploadInBatch;
      uploadFunction();
    };

    createBatch = async () => {
      const { id, description } = this.state;
      const { business, account, match: { params: { type = 'internal' } = {} } = {}, t } = this.props;
      try {
        await BatchRepo.create(id, { business, account, description, type: batchType[type] });
        this.processItems();
      } catch (error) {
        console.error('ERROR: createBatch() response: ', error.response);
        message.error(t('error.creating_batch'));
      }
    };

    processBatch = (records, description) => {
      this.setState({
        processing: true,
        items: records,
        description,
      }, () => {
        this.createBatch();
      });
    };

    render() {
      const { t, account, match: { params: { type = 'internal' } = {} } = {} } = this.props;

      if (this.state.redirect) {
        return <Redirect to={URL(`/accounts/${account.id}/batches/${this.state.id}`, this.props)} />;
      }

      if (this.state.processing) {
        return (
          <>
            <Title level={3}>{this.state.description}</Title>
            <BatchStats
              items={this.state.items}
              ok={this.state.processed.filter(({ status }) => (status === transactionStatuses.success)).length}
              nok={this.state.processed.filter(({ status }) => (status === transactionStatuses.failed)).length}
              processed={this.state.processed.length}
              balance={money.toDisplay(this.props.account.balance.available, this.props.account.region_id)}
            />
            <Alert
              message="Batch details"
              description={t('batch.info1')}
              type="info"
              showIcon
            />
            <Alert
              description={t('batch.info2')}
              type="info"
              showIcon
              closeText="x"
            />
          </>
        );
      }

      return (
        <>
          <Title level={3}>
            {t('create_batch')}
            <Button.Group style={{ float: 'right' }}>
              <Button onClick={() => { window.location = csvExample[type]; }}><Icon type="download" /> {t('batch.download_example')}</Button>
            </Button.Group>
          </Title>
          <CreationForm
            onSubmit={this.processBatch}
            validate={validate[type]}
            batchOptions={csvOptions[type]}
            summaryColumns={summaryColumns[type]}
          />
        </>
      );
    }
}

BatchNew.propTypes = {
  account: PropTypes.shape({
    balance: PropTypes.shape({
      available: PropTypes.any,
    }),
    id: PropTypes.any,
    region_id: PropTypes.any,
  }),
  business: PropTypes.shape({
    id: PropTypes.any,
  }),
  t: PropTypes.func,
  match: PropTypes.shape({
    params: PropTypes.shape({
      type: PropTypes.string,
    }),
  }),
};

export { BatchNew };
export default subscribe(withTranslation()(BatchNew), ['business', 'account']);
